// src/PortalHeadManager.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../api/axios";
import { useAuth } from "../auth/AuthContext";

/** Alleen /login/:companyId uit path halen */
function getCompanyIdFromPath(pathname) {
  const m = pathname.match(/^\/login\/(\d+)(\/|$)/);
  return m ? Number(m[1]) : null;
}

/** Reset-password token uit path halen */
function getResetPasswordTokenFromPath(pathname) {
  const m = pathname.match(/^\/reset-password\/([^/]+)(\/|$)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Accept-invite token uit path halen */
function getAcceptInviteTokenFromPath(pathname) {
  const m = pathname.match(/^\/acceptinvite\/([^/]+)(\/|$)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Accept-invite companyId uit /acceptinvite/:token/:companyId halen */
function getAcceptInviteCompanyIdFromPath(pathname) {
  const m = pathname.match(/^\/acceptinvite\/[^/]+\/(\d+)(\/|$)/);
  return m ? Number(m[1]) : null;
}

/** Maak van favicon_path altijd een absolute URL (S3), nooit localhost-relative */
function buildFaviconUrl(favicon_path) {
  if (!favicon_path) return null;

  if (/^https?:\/\//i.test(favicon_path)) return favicon_path;

  const S3_BASE =
    process.env.REACT_APP_S3_BASE_URL ||
    "https://s3-ianalytics.s3.eu-west-3.amazonaws.com";

  const key = String(favicon_path).replace(/^\/+/, "");
  return `${S3_BASE}/${encodeURI(key)}`;
}

function detectMimeFromUrl(url) {
  const u = (url || "").toLowerCase();
  if (u.endsWith(".ico") || u.includes(".ico?")) return "image/x-icon";
  if (u.endsWith(".png") || u.includes(".png?")) return "image/png";
  if (u.endsWith(".svg") || u.includes(".svg?")) return "image/svg+xml";
  return "";
}

function setTitle(title) {
  if (!title) return;
  document.title = title;
}

function getOrCreateFaviconLinks() {
  const head = document.head;

  let icon = document.getElementById("app-favicon");
  if (!(icon instanceof HTMLLinkElement)) {
    icon = document.createElement("link");
    icon.id = "app-favicon";
    icon.rel = "icon";
    head.appendChild(icon);
  } else {
    icon.rel = "icon";
  }

  let shortcut = document.getElementById("app-favicon-shortcut");
  if (!(shortcut instanceof HTMLLinkElement)) {
    shortcut = document.createElement("link");
    shortcut.id = "app-favicon-shortcut";
    shortcut.rel = "shortcut icon";
    head.appendChild(shortcut);
  } else {
    shortcut.rel = "shortcut icon";
  }

  return { icon, shortcut };
}

function setFavicon(absHref) {
  if (!absHref) return;

  let finalHref = absHref;

  try {
    const url = new URL(absHref);
    url.searchParams.set("v", String(Date.now()));
    finalHref = url.toString();
  } catch {
    finalHref = `${absHref}${absHref.includes("?") ? "&" : "?"}v=${Date.now()}`;
  }

  const type = detectMimeFromUrl(finalHref);
  const { icon, shortcut } = getOrCreateFaviconLinks();

  icon.href = finalHref;
  shortcut.href = finalHref;

  if (type) {
    icon.type = type;
    shortcut.type = type;
  } else {
    icon.removeAttribute("type");
    shortcut.removeAttribute("type");
  }
}

function extractPortalHeadFromBranding(branding) {
  if (!branding) return { portalTitle: null, faviconPath: null };

  const portalTitle =
    branding?.portal_title ??
    branding?.branding?.portal_title ??
    branding?.portal?.portal_title ??
    null;

  const faviconPath =
    branding?.favicon_path ??
    branding?.branding?.favicon_path ??
    branding?.portal?.favicon_path ??
    null;

  return { portalTitle, faviconPath };
}

export default function PortalHeadManager() {
  const { pathname } = useLocation();

  const auth = useAuth();
  const user = auth?.user ?? null;
  const authCompanyId = auth?.companyId ?? null;

  const routeCompanyId = useMemo(() => getCompanyIdFromPath(pathname), [pathname]);

  const resetPasswordToken = useMemo(
    () => getResetPasswordTokenFromPath(pathname),
    [pathname]
  );

  const acceptInviteToken = useMemo(
    () => getAcceptInviteTokenFromPath(pathname),
    [pathname]
  );

  const acceptInviteCompanyId = useMemo(
    () => getAcceptInviteCompanyIdFromPath(pathname),
    [pathname]
  );

  const [publicContext, setPublicContext] = useState(null);

  const effectiveCompanyId = useMemo(() => {
    if (routeCompanyId != null) return routeCompanyId;

    // Belangrijk: deze komt uit /acceptinvite/:token/:companyId
    // en werkt dus ook als de invite-token al gebruikt/verlopen is.
    if (acceptInviteCompanyId != null) return acceptInviteCompanyId;

    if (publicContext?.company_id != null) return Number(publicContext.company_id);
    if (publicContext?.invitedtocompanyId != null) {
      return Number(publicContext.invitedtocompanyId);
    }

    if (authCompanyId != null) return Number(authCompanyId);
    if (user?.default_companyid != null) return Number(user.default_companyid);

    return null;
  }, [
    routeCompanyId,
    acceptInviteCompanyId,
    publicContext?.company_id,
    publicContext?.invitedtocompanyId,
    authCompanyId,
    user?.default_companyid,
  ]);

  const seqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const contextToken = resetPasswordToken || acceptInviteToken;

    if (!contextToken) {
      setPublicContext(null);
      return;
    }

    const endpoint = resetPasswordToken
      ? `/api/public/reset-password-context/${contextToken}`
      : `/api/verify-token-acceptinvite/${contextToken}`;

    (async () => {
      try {
        const { data } = await api.get(endpoint);

        if (!cancelled) {
          setPublicContext(data || null);
        }
      } catch {
        if (!cancelled) {
          setPublicContext(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resetPasswordToken, acceptInviteToken]);

  useEffect(() => {
    let cancelled = false;
    const seq = ++seqRef.current;

    if (!effectiveCompanyId || Number(effectiveCompanyId) === 0) return;

    (async () => {
      try {
        if (publicContext?.branding) {
          const { portalTitle, faviconPath } = extractPortalHeadFromBranding(
            publicContext.branding
          );

          if (cancelled || seq !== seqRef.current) return;

          const finalPortalTitle =
            portalTitle ||
            publicContext?.portal_title ||
            publicContext?.branding?.portal_title;

          const finalFaviconPath =
            faviconPath ||
            publicContext?.favicon_path ||
            publicContext?.branding?.favicon_path;

          if (finalPortalTitle) setTitle(finalPortalTitle);

          const favAbs = buildFaviconUrl(finalFaviconPath);
          if (favAbs) setFavicon(favAbs);

          return;
        }

        const b = user?.branding || null;
        const userHead = extractPortalHeadFromBranding(b);

        if (userHead.portalTitle || userHead.faviconPath) {
          if (cancelled || seq !== seqRef.current) return;

          if (userHead.portalTitle) setTitle(userHead.portalTitle);

          const favAbs = buildFaviconUrl(userHead.faviconPath);
          if (favAbs) setFavicon(favAbs);

          return;
        }

        const { data } = await api.get("/public/company-branding", {
          params: { companyId: effectiveCompanyId },
        });

        if (cancelled || seq !== seqRef.current) return;

        const portalTitle =
          data?.portal_title ??
          data?.branding?.portal_title ??
          data?.portal?.portal_title ??
          null;

        const faviconPath =
          data?.favicon_path ??
          data?.branding?.favicon_path ??
          data?.portal?.favicon_path ??
          null;

        if (portalTitle) setTitle(portalTitle);

        const favAbs = buildFaviconUrl(faviconPath);
        if (favAbs) setFavicon(favAbs);
      } catch {
        // defaults laten staan
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyId, publicContext, user, pathname]);

  return null;
}